/** Word-level diff for short prose (project questions): LCS over words, whitespace kept. */
export type DiffPart = { kind: "same" | "del" | "ins"; text: string };

/** Above this many LCS cells, skip the table and show old → new whole. */
const MAX_CELLS = 1_000_000;

const tokens = (s: string) => s.split(/(\s+)/).filter(Boolean);

/** Same words, ignoring whitespace differences. */
export function sameWords(a: string, b: string): boolean {
  return a.trim().split(/\s+/).join(" ") === b.trim().split(/\s+/).join(" ");
}

export function wordDiff(before: string, after: string): DiffPart[] {
  const a = tokens(before);
  const b = tokens(after);
  if (a.length * b.length > MAX_CELLS) {
    return [{ kind: "del", text: before }, { kind: "ins", text: after }].filter((p) => p.text) as DiffPart[];
  }
  // lcs[i][j] = LCS length of a[i:] and b[j:].
  const lcs = Array.from({ length: a.length + 1 }, () => new Uint32Array(b.length + 1));
  for (let i = a.length - 1; i >= 0; i--) {
    for (let j = b.length - 1; j >= 0; j--) {
      lcs[i][j] = a[i] === b[j] ? lcs[i + 1][j + 1] + 1 : Math.max(lcs[i + 1][j], lcs[i][j + 1]);
    }
  }
  const out: DiffPart[] = [];
  const push = (kind: DiffPart["kind"], text: string) => {
    const last = out[out.length - 1];
    if (last?.kind === kind) last.text += text;
    else out.push({ kind, text });
  };
  let i = 0;
  let j = 0;
  while (i < a.length && j < b.length) {
    if (a[i] === b[j]) { push("same", a[i]); i++; j++; }
    else if (lcs[i + 1][j] >= lcs[i][j + 1]) push("del", a[i++]);
    else push("ins", b[j++]);
  }
  while (i < a.length) push("del", a[i++]);
  while (j < b.length) push("ins", b[j++]);
  return out;
}
